import React, { createContext, useContext, useState, useEffect } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { Api } from "../Api";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { keycloak, initialized } = useKeycloak();

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const response = await Api.get("/user/info");
      setUserInfo(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching user info:", err);
      setError(err);
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch user info after Keycloak is initialized and user is authenticated
    if (initialized && keycloak.authenticated && keycloak.token) {
      console.log("UserContext: Keycloak authenticated, fetching user info");
      fetchUserInfo();
    } else if (initialized && !keycloak.authenticated) {
      // User not authenticated, clear user info
      console.log("UserContext: User not authenticated, clearing state");
      setUserInfo(null);
      setLoading(false);
      setError(null);
    } else {
      // Still initializing, keep loading state
      console.log("UserContext: Keycloak still initializing, waiting...");
      setLoading(true);
    }
  }, [initialized, keycloak.authenticated, keycloak.token]);

  // Function to refresh user info when needed (e.g., after organization switch)
  const refreshUserInfo = () => {
    fetchUserInfo();
  };

  const value = {
    userInfo,
    loading,
    error,
    refreshUserInfo,
    isAdmin: userInfo?.is_admin || false,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export default UserContext;
